import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress } from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import LinkIcon from '@mui/icons-material/Link';
import { apiFetch } from './api';

export default function ImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  const handleClose = () => {
    setFile(null);
    setError('');
    setImportResult(null);
    setLinkResult(null);
    setLinking(false);
    onClose();
  };

  const handleLinkPlayers = async () => {
    setLinking(true);
    setError('');
    try {
      const result = await apiFetch('/players-link-tournament-players', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      setLinkResult(result);
    } catch (err) {
      setError(err.message || 'Verknüpfung fehlgeschlagen.');
    } finally {
      setLinking(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Bitte wählen Sie eine CSV-Datei aus.');
      return;
    }
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const result = await apiFetch('/players-import-csv', {
        method: 'POST',
        body: formData
      });
  setImportResult(result.imported);
  if (onImported) onImported();
    } catch (err) {
      setError(err.message || 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog open={open && !importResult} onClose={handleClose}>
        <DialogTitle>Import Meldekartei</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<ImportExportIcon />}
              sx={{ mb: 2 }}
            >
              Datei auswählen
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {file && (
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 250,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}
              >
                {file.name}
              </Typography>
            )}
            {error && <Typography color="error">{error}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>Abbrechen</Button>
          <Button onClick={handleUpload} disabled={uploading || !file} variant="contained" color="primary">
            {uploading ? 'Importiere...' : 'Importieren'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!importResult} onClose={handleClose}>
        <DialogTitle>Import abgeschlossen</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {importResult === 1
              ? '1 Spieler wurde importiert.'
              : `${importResult} Spieler wurden importiert.`}
          </Typography>
          {!linkResult && (
            <Typography variant="body2" color="text.secondary">
              Möchten Sie jetzt Turnierspieler mit den importierten Spielern verknüpfen?
            </Typography>
          )}
          {linkResult && (
            <Box sx={{ mt: 2 }}>
              <Typography color="success.main" sx={{ mb: 1 }}>
                ✓ {linkResult.linked} Turnierspieler verknüpft
              </Typography>
              {linkResult.still_unmatched > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {linkResult.still_unmatched} Turnierspieler konnten nicht verknüpft werden
                </Typography>
              )}
            </Box>
          )}
          {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          {!linkResult && (
            <>
              <Button onClick={handleClose} disabled={linking}>
                Überspringen
              </Button>
              <Button 
                onClick={handleLinkPlayers} 
                disabled={linking}
                variant="contained" 
                color="primary"
                startIcon={linking ? <CircularProgress size={16} /> : <LinkIcon />}
              >
                {linking ? 'Verknüpfe...' : 'Verknüpfen'}
              </Button>
            </>
          )}
          {linkResult && (
            <Button onClick={handleClose} autoFocus variant="contained">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
